import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAllClassifiedContactPolicies } from '../../../../queries/Contact/useAllClassifiedContactPolicies';
import { useInsurancePolicyCarrierAutocompleteQuery } from '../../../../queries/InsurancePolicy/useInsurancePolicyCarrierAutocomplete';
import { useInsurancePolicyLineOfBusinessAutocompleteQuery } from '../../../../queries/InsurancePolicy/useInsurancePolicyLineOfBusinessAutocomplete';
import { Checkbox } from '../../Checkbox';
import { Dropdown } from '../../Dropdown';
import { DisclosureIcon } from '../../svgs/icons/DisclosureIcon';
import { styleSheet } from './styles';

export interface IPolicyActionableFilterProps {
	filter: Api.IPolicyActionableFilter;
	onFilterChanged: (filter: Api.IPolicyActionableFilter) => void;
	qualifier?: string;
}

import {
	groupLobsByCategory,
	hasCategoryGrouping,
	isCategoryFullySelected,
	isCategoryPartiallySelected,
	toggleCategorySelection,
} from '../../dataBoards/policies/lobGrouping';

/**
 * Filter panel for UpcomingPolicyRenewals resource selector.
 * Provides line of business (grouped by commercial/personal), carrier,
 * and premium range filters.
 */
export function PolicyActionableFilter({ filter, onFilterChanged, qualifier }: IPolicyActionableFilterProps) {
	const userSession = useUserSession();
	// Premium/LOB/carrier data comes from the PolicyBoard integration specifically. Legacy accounts
	// can have policiesEnabled=true with policyBoard.enabled=false (renewals via key facts on
	// contacts, no board), so gate strictly on policyBoard.enabled — the loose OR would surface
	// filter fields the account has no data for.
	const policyBoardEnabled = !!userSession.account?.features?.boards?.policyBoard?.enabled;

	const [isExpanded, setIsExpanded] = useState(false);
	const [isLobDropdownOpen, setIsLobDropdownOpen] = useState(false);
	const [isCarrierDropdownOpen, setIsCarrierDropdownOpen] = useState(false);

	// Load flat LOB list from policy board
	const lobAutocompleteQuery = useInsurancePolicyLineOfBusinessAutocompleteQuery({
		enabled: true,
		pageSize: 100,
		fragment: '',
	});

	const flatLobs = useMemo(() => {
		return lobAutocompleteQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [lobAutocompleteQuery.data]);

	// Load classified grouping (Commercial/Personal/Other) for category headers
	const { data: classifiedPolicies } = useAllClassifiedContactPolicies({
		contactsRequest: { qualifier },
		enabled: true,
	});

	const lobCategories = useMemo(
		() => groupLobsByCategory(flatLobs, classifiedPolicies),
		[flatLobs, classifiedPolicies]
	);
	const hasCategories = hasCategoryGrouping(lobCategories);

	// Carrier list
	const carrierAutocompleteQuery = useInsurancePolicyCarrierAutocompleteQuery({
		enabled: true,
		pageSize: 25,
		fragment: '',
	});

	const carriers = useMemo(() => {
		return carrierAutocompleteQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [carrierAutocompleteQuery.data]);

	const updateFilter = useCallback(
		(updates: Partial<Api.IPolicyActionableFilter>) => {
			const next = { ...filter, ...updates };
			// Drop cleared keys entirely — the composer VM decides whether to attach a filter object
			// via Object.keys(...).length, so a lingering { premiumMin: undefined } reads as an
			// active filter even though nothing is set.
			for (const key of Object.keys(next) as (keyof Api.IPolicyActionableFilter)[]) {
				if (next[key] === undefined) {
					delete next[key];
				}
			}
			onFilterChanged(next);
		},
		[filter, onFilterChanged]
	);

	// --- Line of Business ---
	const onToggleLob = (lobName: string) => {
		const selected = filter.linesOfBusiness || [];
		const currentIndex = selected.indexOf(lobName);
		const next = [...selected];

		if (currentIndex === -1) {
			next.push(lobName);
		} else {
			next.splice(currentIndex, 1);
		}
		updateFilter({ linesOfBusiness: next.length > 0 ? next : undefined });
	};

	const onToggleCategory = (lobsInCategory: Api.IInsurancePolicyLineOfBusiness[]) => {
		const next = toggleCategorySelection(lobsInCategory, filter.linesOfBusiness || [], lob => lob.name);
		updateFilter({ linesOfBusiness: next.length > 0 ? next : undefined });
	};

	const lobDropdownMessage = useMemo(() => {
		if (!filter.linesOfBusiness?.length) {
			return 'Line of Business';
		}
		return filter.linesOfBusiness.length === 1
			? filter.linesOfBusiness[0]
			: `${filter.linesOfBusiness[0]} or ${filter.linesOfBusiness.length - 1} more`;
	}, [filter.linesOfBusiness]);

	const resetLobs = () => {
		updateFilter({ linesOfBusiness: undefined });
	};

	// --- Carrier ---
	const onToggleCarrier = (carrier: Api.IInsurancePolicyCarrier) => {
		const selected = filter.carriers || [];
		const currentIndex = selected.indexOf(carrier.name);
		const next = [...selected];

		if (currentIndex === -1) {
			next.push(carrier.name);
		} else {
			next.splice(currentIndex, 1);
		}
		updateFilter({ carriers: next.length > 0 ? next : undefined });
	};

	const carrierDropdownMessage = useMemo(() => {
		if (!filter.carriers?.length) {
			return 'Carrier';
		}
		return filter.carriers.length === 1
			? filter.carriers[0]
			: `${filter.carriers[0]} or ${filter.carriers.length - 1} more`;
	}, [filter.carriers]);

	const resetCarriers = () => {
		updateFilter({ carriers: undefined });
	};

	// --- Premium range ---
	// Committed on a debounce (and on blur) rather than per keystroke — every filter commit resets
	// and reloads the recipients list and refetches renewal policies, so typing "1500" would
	// otherwise fire four full reloads.
	const [premiumMinInput, setPremiumMinInput] = useState(filter.premiumMin?.toString() ?? '');
	const [premiumMaxInput, setPremiumMaxInput] = useState(filter.premiumMax?.toString() ?? '');
	const premiumCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const commitPremium = useCallback(
		(minInput: string, maxInput: string) => {
			if (premiumCommitTimeoutRef.current) {
				clearTimeout(premiumCommitTimeoutRef.current);
				premiumCommitTimeoutRef.current = null;
			}
			const premiumMin = minInput ? Number(minInput) : undefined;
			const premiumMax = maxInput ? Number(maxInput) : undefined;
			if (premiumMin === filter.premiumMin && premiumMax === filter.premiumMax) {
				return;
			}
			updateFilter({ premiumMin, premiumMax });
		},
		[filter.premiumMin, filter.premiumMax, updateFilter]
	);

	const onPremiumInputChange = (field: 'min' | 'max', value: string) => {
		const nextMin = field === 'min' ? value : premiumMinInput;
		const nextMax = field === 'max' ? value : premiumMaxInput;
		setPremiumMinInput(nextMin);
		setPremiumMaxInput(nextMax);
		if (premiumCommitTimeoutRef.current) {
			clearTimeout(premiumCommitTimeoutRef.current);
		}
		premiumCommitTimeoutRef.current = setTimeout(() => commitPremium(nextMin, nextMax), 600);
	};

	useEffect(() => {
		return () => {
			if (premiumCommitTimeoutRef.current) {
				clearTimeout(premiumCommitTimeoutRef.current);
			}
		};
	}, []);

	// Clear any orphaned premium values from a prior session when the account no longer runs
	// PolicyBoard. The inputs are hidden below, so the user can't clear the values themselves
	// and they would silently filter every renewal to zero.
	useEffect(() => {
		if (policyBoardEnabled) {
			return;
		}
		if (filter.premiumMin != null || filter.premiumMax != null) {
			setPremiumMinInput('');
			setPremiumMaxInput('');
			updateFilter({ premiumMin: undefined, premiumMax: undefined });
		}
	}, [policyBoardEnabled, filter.premiumMin, filter.premiumMax, updateFilter]);

	// --- Clear all ---
	const handleClearAll = () => {
		if (premiumCommitTimeoutRef.current) {
			clearTimeout(premiumCommitTimeoutRef.current);
			premiumCommitTimeoutRef.current = null;
		}
		setPremiumMinInput('');
		setPremiumMaxInput('');
		onFilterChanged({});
	};

	const showLobDropdown = flatLobs.length > 0;
	const showCarrierDropdown = carriers.length > 0;
	const showPremiumRange = policyBoardEnabled;

	const hasActiveFilters =
		filter.linesOfBusiness?.length > 0 ||
		filter.carriers?.length > 0 ||
		(showPremiumRange && (filter.premiumMin != null || filter.premiumMax != null));

	const activeFilterCount = [
		filter.linesOfBusiness?.length > 0,
		filter.carriers?.length > 0,
		showPremiumRange && (filter.premiumMin != null || filter.premiumMax != null),
	].filter(Boolean).length;

	if (!showLobDropdown && !showCarrierDropdown && !showPremiumRange) {
		return null;
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.headerRow)}>
				<button type='button' className={css(styleSheet.expandToggle)} onClick={() => setIsExpanded(!isExpanded)}>
					<DisclosureIcon className={css(styleSheet.expandIcon, isExpanded ? styleSheet.expandIconOpen : null)} />
					<h5 className={css(styleSheet.heading)}>
						Policy Filters
						{!isExpanded && activeFilterCount > 0 ? (
							<span className={css(styleSheet.filterBadge)}>{activeFilterCount}</span>
						) : null}
						{!isExpanded ? <span className={css(styleSheet.expandHint)}>(click to expand)</span> : null}
					</h5>
				</button>
				{hasActiveFilters && isExpanded ? (
					<button type='button' className={css(styleSheet.clearButton)} onClick={handleClearAll}>
						Clear all
					</button>
				) : null}
			</div>

			{isExpanded ? (
				<div className={css(styleSheet.filterRow)}>
					{/* Line of Business Dropdown */}
					{showLobDropdown ? (
						<Dropdown
							anchor={
								<div className={css(styleSheet.dropdownAnchor)}>
									<span>{lobDropdownMessage}</span>
									<DisclosureIcon className={css(styleSheet.dropdownIcon)} />
								</div>
							}
							className={css(styleSheet.dropdownInline, isLobDropdownOpen ? styleSheet.dropdownInlineOpen : null)}
							contentClassName={css(styleSheet.dropdownMenu)}
							contentPositionY='bottom'
							contentInnerClassName={css(styleSheet.dropdownContentInner)}
							isOpen={isLobDropdownOpen}
							onOpenChanged={open => setIsLobDropdownOpen(open)}
							openOnClick
						>
							<div className={css(styleSheet.dropdownContent)}>
								{hasCategories
									? lobCategories.map(([category, lobs]) => (
											<div key={`lob-category-${category}`}>
												<div className={css(styleSheet.dropdownRow, styleSheet.categoryRow)}>
													<Checkbox
														type='large'
														checked={isCategoryFullySelected(lobs, filter.linesOfBusiness || [], lob => lob.name)}
														partial={isCategoryPartiallySelected(lobs, filter.linesOfBusiness || [], lob => lob.name)}
														id={`lob-category-${category}`}
														onChange={() => onToggleCategory(lobs)}
													/>
													<span className={css(styleSheet.categoryLabel)}>{category}</span>
												</div>
												{lobs.map(lob => (
													<div
														className={css(styleSheet.dropdownRow, styleSheet.nestedRow)}
														key={`lob-select-${lob.id}`}
													>
														<Checkbox
															type='large'
															checked={(filter.linesOfBusiness || []).indexOf(lob.name) > -1}
															id={`lob-filter-${lob.id}`}
															onChange={() => onToggleLob(lob.name)}
														/>
														<span>{lob.name}</span>
													</div>
												))}
											</div>
										))
									: flatLobs.map(lob => (
											<div className={css(styleSheet.dropdownRow)} key={`lob-select-${lob.id}`}>
												<Checkbox
													type='large'
													checked={(filter.linesOfBusiness || []).indexOf(lob.name) > -1}
													id={`lob-filter-${lob.id}`}
													onChange={() => onToggleLob(lob.name)}
												/>
												<span>{lob.name}</span>
											</div>
										))}
								{lobAutocompleteQuery.hasNextPage ? (
									<Waypoint onEnter={() => lobAutocompleteQuery.fetchNextPage()} />
								) : null}
								<div className={css(styleSheet.dropdownSeparator)} />
								<button type='button' className={css(styleSheet.resetButton)} onClick={resetLobs}>
									Reset Filter
								</button>
							</div>
						</Dropdown>
					) : null}

					{/* Carrier Dropdown */}
					{showCarrierDropdown ? (
						<Dropdown
							anchor={
								<div className={css(styleSheet.dropdownAnchor)}>
									<span>{carrierDropdownMessage}</span>
									<DisclosureIcon className={css(styleSheet.dropdownIcon)} />
								</div>
							}
							className={css(styleSheet.dropdownInline, isCarrierDropdownOpen ? styleSheet.dropdownInlineOpen : null)}
							contentClassName={css(styleSheet.dropdownMenu)}
							contentPositionY='bottom'
							contentInnerClassName={css(styleSheet.dropdownContentInner)}
							isOpen={isCarrierDropdownOpen}
							onOpenChanged={open => setIsCarrierDropdownOpen(open)}
							openOnClick
						>
							<div className={css(styleSheet.dropdownContent)}>
								<div>
									{carriers.map(carrier => (
										<div className={css(styleSheet.dropdownRow)} key={`carrier-select-${carrier.id}`}>
											<Checkbox
												type='large'
												checked={(filter.carriers || []).indexOf(carrier.name) > -1}
												id={`carrier-filter-${carrier.id}`}
												onChange={() => onToggleCarrier(carrier)}
											/>
											<span>{carrier.name}</span>
										</div>
									))}
								</div>
								{carrierAutocompleteQuery.hasNextPage ? (
									<Waypoint onEnter={() => carrierAutocompleteQuery.fetchNextPage()} />
								) : null}
								<div className={css(styleSheet.dropdownSeparator)} />
								<button type='button' className={css(styleSheet.resetButton)} onClick={resetCarriers}>
									Reset Filter
								</button>
							</div>
						</Dropdown>
					) : null}

					{/* Premium Range */}
					{showPremiumRange ? (
						<>
							<div className={css(styleSheet.filterField)}>
								<label className={css(styleSheet.label)}>Min Premium ($)</label>
								<input
									className={css(styleSheet.input)}
									min={0}
									placeholder='0'
									type='number'
									value={premiumMinInput}
									onChange={e => onPremiumInputChange('min', e.target.value)}
									onBlur={() => commitPremium(premiumMinInput, premiumMaxInput)}
								/>
							</div>
							<div className={css(styleSheet.filterField)}>
								<label className={css(styleSheet.label)}>Max Premium ($)</label>
								<input
									className={css(styleSheet.input)}
									min={0}
									placeholder='No limit'
									type='number'
									value={premiumMaxInput}
									onChange={e => onPremiumInputChange('max', e.target.value)}
									onBlur={() => commitPremium(premiumMinInput, premiumMaxInput)}
								/>
							</div>
						</>
					) : null}
				</div>
			) : null}
		</div>
	);
}
